import { MIMETYPE_JSON } from '@disy/lib/media-type';

import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege.gen';
import { getDataSourceFlavorOrDataSourceTypeAsText } from 'cadenza/admin/datasource-filter/datasource-flavor-or-datasource-type-utils';
import type { RepositoryImportResultDto } from 'cadenza/admin/dto/repository-import-result-dto';
import type { RepositoryExportCdsDataExportOption } from 'cadenza/admin/model/repository-export-cds-data-export-option.gen';
import type { RepositoryImporterAuthorizationOption } from 'cadenza/admin/model/repository-importer-authorization-option.gen';
import { ajax } from 'cadenza/api-client/api';
import type { CreateNewDatasourceDto } from 'cadenza/api-client/dto/create-new-datasource-dto';
import type { DataSourceDto, DataSourceListingDto, MutableDataSourceDto } from 'cadenza/api-client/dto/data-source-dto';
import type { DataSourceTemplateDto } from 'cadenza/api-client/dto/data-source-template-dto';
import type { RepositoryImportDto } from 'cadenza/api-client/dto/repository-import-dto';
import type { UploadedFileDto } from 'cadenza/api-client/dto/uploaded-file-dto';
import type { WorkOrderProgressEvent } from 'cadenza/api-client/dto/workorder-dto';
import type { DataSourceField } from 'cadenza/api-client/model/data-source-field.gen';
import type { TableData, TableParams } from 'cadenza/api-client/tables-api';
import { tableParams } from 'cadenza/api-client/tables-api';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import { startWorkOrder } from 'cadenza/api-client/workorder-api';
import type { DataSourceId } from 'cadenza/repository/model/data-source-id.gen';
import type { DataSourceTableDto } from 'cadenza/repository/model/data-source-table-dto';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { RepositorySchemaId } from 'cadenza/repository/repository-schema-id';

export interface PathElement {
  name: string;
  labels?: string[];
}

/** @see net/disy/cadenza/repository/web/rest/dto/DataSourceVersionStatus.java */
type DataSourceVersionStatus = 'UNCHANGED' | 'UPGRADED' | 'DOWNGRADED';

export interface DataSourceStatusDto {
  canConnect: boolean;
  canCopyData?: boolean;
  version?: string;
  versionStatus?: DataSourceVersionStatus;
  versionAdjusted?: boolean;
  cdsSchemaValid?: boolean;
}

/** @see net/disy/cadenza/repository/web/rest/ObjecttypesEndpointController.java*/
export type AttributeFilter = 'ALL' | 'USER_BASED_RESTRICTION_COMPATIBLE';

/** @see net/disy/cadenza/repository/api/datasource/DataSourceItemUsageType.java*/
type DataSourceItemUsageType = 'OBJECTTYPE' | 'WORKBOOK' | 'CATALOG_LAYER' | 'CURATED_MAP';

export type DataSourceItemUsageDto = {
  [key in DataSourceItemUsageType]?: number;
};

export function getDataSourceItemUsageAnalysis(dataSourceId: DataSourceId) {
  return ajax<DataSourceItemUsageDto>(`${dataSourceUrl(dataSourceId)}/usages`, { dataType: 'json' });
}

function addTypePrintName<T extends DataSourceListingDto>(ds: T): T {
  ds.typePrintName = ds.type ? getDataSourceFlavorOrDataSourceTypeAsText(ds.type, ds.flavor) : '';
  return ds;
}

export function getDataSources(privilege?: ObjectPrivilege) {
  const urlParams = new URLSearchParams({
    ...(privilege && { privilege }),
  });
  return ajax<DataSourceListingDto[]>(`/datasources?${urlParams}`, { dataType: 'json' }).then((dataSources) =>
    dataSources.map(addTypePrintName),
  );
}

export function getDatasourceTables(dataSourceId: DataSourceId, params: TableParams, abortSignal?: AbortSignal) {
  return ajax<TableData<DataSourceTableDto>>(`${dataSourceUrl(dataSourceId)}/tables?${tableParams(params)}`, {
    dataType: 'json',
    signal: abortSignal,
  });
}

export function getDataSourceTemplates() {
  return ajax<DataSourceTemplateDto[]>('/datasource-templates', { dataType: 'json' });
}

export function createNewDatasource(createNewDatasourceDto: CreateNewDatasourceDto, migrateIfNeeded?: boolean) {
  let url = '/datasources';
  if (migrateIfNeeded) {
    url += '?migrateIfNeeded=true';
  }
  return ajax<DataSourceDto>(url, {
    method: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(createNewDatasourceDto),
  });
}

export function testNewDatasourceConnection(createNewDatasourceDto: CreateNewDatasourceDto) {
  return ajax<DataSourceStatusDto>('/datasource-status', {
    method: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(createNewDatasourceDto),
    dataType: 'json',
  });
}

/**
 * Gets a datasource details dto from backend.
 *
 * @param dataSourceId - data source id
 * @param includeFields - Array of fields to be filled in the dto object. Leave empty if you want all fields to be filled.
 * @return DataSourceDto object
 */
export function getDatasourceDetails(dataSourceId: DataSourceId, includeFields?: readonly DataSourceField[]) {
  return ajax<DataSourceDto>(dataSourceUrl(dataSourceId), {
    dataType: 'json',
    data: {
      includeFields: includeFields ? includeFields.join(',') : '',
    },
  }).then(addTypePrintName);
}

export async function testExistingDatasourceConnection(dataSourceId: DataSourceId) {
  return ajax<DataSourceStatusDto>(`${dataSourceUrl(dataSourceId)}/status`, { dataType: 'json' });
}

export function deleteDatasource(dataSourceId: DataSourceId) {
  return ajax<void>(dataSourceUrl(dataSourceId), { method: 'DELETE' });
}

/**
 * Updates a data source, only changed fields are written.
 *
 * @param id - ID of the data source to update
 * @param patch - Sparse details of the data source
 * @param migrateIfNeeded - Whether the migration of the CDS schema has been confirmed by the user
 * @return Resolves when the data source is updated. Rejects otherwise.
 */
export function updateDataSource(id: DataSourceId, patch: Partial<MutableDataSourceDto>, migrateIfNeeded?: boolean) {
  let url = dataSourceUrl(id);
  if (migrateIfNeeded) {
    url += '?migrateIfNeeded=true';
  }
  return ajax<DataSourceDto>(url, createPatch(patch)).then(addTypePrintName);
}

function dataSourceUrl({ repositoryName, itemName }: DataSourceId) {
  return `/repositories/${repositoryName}/datasources/${itemName}`;
}

export type AuthorizationExportOption = 'PLAIN' | 'GROUPS' | 'GROUPS_AND_USERS' | 'FULL'; // see AuthorizationExportOption.java

export interface RepositoryExportOptions {
  authorizations: AuthorizationExportOption;
  importerAuthorizationOption: RepositoryImporterAuthorizationOption;
  cdsDataExportOption: RepositoryExportCdsDataExportOption;
}

/**
 * Starts export of given database repository.
 *
 * @param repositoryName - technical name of repository that should be exported
 * @param exportOptions - Options for the repository export
 * @return - Promise with workOrderId
 */
export function exportRepository(repositoryName: RepositoryName, exportOptions: RepositoryExportOptions) {
  return ajax<WorkOrderId>(`/repositories/${repositoryName}/export-work-orders`, {
    type: 'POST',
    data: JSON.stringify(exportOptions),
    contentType: MIMETYPE_JSON,
  });
}

/**
 * Gets repository descriptor from uploaded file.
 *
 * @param fileName - name of the file uploaded to the server
 * @param signature - signature of the file uploaded to the server
 * @param abortSignal - A signal to abort the request
 * @return promise with repositoryDescriptor
 */
export function parseRepositoryPackage({ fileName, signature }: UploadedFileDto, signal?: AbortSignal) {
  const urlParams = new URLSearchParams({ signature });
  return ajax<RepositoryImportDto>(`/files/${fileName}/repository?${urlParams}`, { signal });
}

export function importRepository(
  { fileName, signature }: UploadedFileDto,
  repositorySchemaId: RepositorySchemaId,
  options: Parameters<typeof startWorkOrder<never, WorkOrderProgressEvent>>[2],
) {
  const urlParams = new URLSearchParams({ fileName, signature, repositorySchemaId });
  return startWorkOrder<RepositoryImportResultDto>(
    `/repositories/importWorkOrders?${urlParams}`,
    'import-repository-result',
    options,
  );
}

export function createPatch(patch: unknown) {
  return {
    type: 'PATCH',
    data: JSON.stringify(patch),
    contentType: MIMETYPE_JSON,
  };
}

/**
 * Starts duplication of given database repository.
 *
 * @param originalRepositoryName - technical name of repository that should be duplicated
 * @param duplicateRepositoryPrintName - the print name of the duplicate repository
 * @param duplicateRepositorySchemaId - the schema id of the duplicate repository
 * @return - Promise with workOrderId
 */
export function duplicateRepository(
  originalRepositoryName: RepositoryName,
  duplicateRepositoryPrintName: string,
  duplicateRepositorySchemaId: RepositorySchemaId,
) {
  const urlParams = new URLSearchParams({
    duplicateRepositoryPrintName,
    duplicateRepositorySchemaId,
  });
  return ajax<RepositoryName>(`/repositories/${originalRepositoryName}/duplicate-work-orders?${urlParams}`, {
    type: 'POST',
    contentType: MIMETYPE_JSON,
  });
}

export function reorderRepository(repositoryNameToMove: RepositoryName, nextRepository?: RepositoryName) {
  return ajax<void>(`/repositories/${repositoryNameToMove}/next-sibling`, {
    method: 'PUT',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(nextRepository),
  });
}
